import { useRef, useState } from "react";
import { PageCanvas } from "@/components/page-canvas";
import { Form, Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Errors } from "../requests/controls";
import ProposalContent from "./content";
import type { Proposal } from "./types";

export default function ProposalView({
  proposal,
  decision_url,
}: {
  proposal: Proposal;
  decision_url: string;
}) {
  const { t } = useTranslation();
  const { customer_history_path } = usePage<{
    customer_history_path?: string;
  }>().props;
  const trigger = useRef<HTMLButtonElement>(null);
  const [secondary, setSecondary] = useState<string | null>(null);
  const { flash } = usePage<{ flash: { alert?: string; notice?: string } }>()
    .props;
  const terminal = ["accepted", "declined"].includes(proposal.state);
  const expiry = proposal.hold_expires_at
    ? new Intl.DateTimeFormat(proposal.representation.locale, {
        dateStyle: "medium",
        timeStyle: "short",
        timeZone: proposal.representation.schedule.time_zone,
      }).format(new Date(proposal.hold_expires_at))
    : null;
  return (
    <PageCanvas as="main">
      {customer_history_path && (
        <Link className="inline-block underline" href={customer_history_path}>
          {t("customer_portal.history")}
        </Link>
      )}
      <Head title={t("proposals.title")} />
      {flash.alert && (
        <p role="alert" className="rounded-md border p-3">
          {flash.alert}
        </p>
      )}
      {flash.notice && (
        <p role="status" className="rounded-md border p-3">
          {flash.notice}
        </p>
      )}
      <Errors />
      <ProposalContent representation={proposal.representation} />
      {terminal ? (
        <p role="status" className="rounded-md border p-4">
          {t(`proposals.${proposal.state}_status`)}
        </p>
      ) : (
        <>
          {proposal.requires_republication ? (
            <p role="status" className="rounded-md border p-4">
              {t("proposals.legacy_help")}
            </p>
          ) : (
            <section
              className="space-y-2 rounded-lg border p-4"
              aria-label={t("proposals.hold")}
            >
              <h2 className="font-semibold">{t("proposals.hold")}</h2>
              <p>
                {t(
                  proposal.hold_active
                    ? "proposals.held_until"
                    : "proposals.expired",
                  { expiry },
                )}
              </p>
              <p className="text-muted-foreground text-sm">
                {t("proposals.expiry_help")}
              </p>
            </section>
          )}
          {proposal.state === "rescheduled" && (
            <p role="status" className="rounded-md border p-4">
              {t("proposals.suggestion_status", {
                time: proposal.suggested_time,
              })}
            </p>
          )}
          {proposal.writable ? (
            <section className="space-y-6" aria-label={t("proposals.decision")}>
              <p className="text-muted-foreground text-sm">
                {t("proposals.accept_help")}
              </p>
              <Form
                key={`accept-${proposal.revision}`}
                action={decision_url}
                method="post"
              >
                {({ processing }) => (
                  <>
                    <input
                      type="hidden"
                      name="revision"
                      value={proposal.revision}
                    />
                    <input
                      type="hidden"
                      name="decision[action]"
                      value="accepted"
                    />
                    <Button type="submit" disabled={processing}>
                      {t("proposals.accept")}
                    </Button>
                  </>
                )}
              </Form>
              <div className="flex flex-wrap gap-3">
                {
                  <Button
                    type="button"
                    variant="outline"
                    aria-expanded={secondary === "rescheduled"}
                    aria-controls="proposal-secondary-rescheduled"
                    onClick={(event) => {
                      trigger.current = event.currentTarget;
                      setSecondary("rescheduled");
                    }}
                  >
                    {t("proposals.suggest")}
                  </Button>
                }
                {
                  <Button
                    type="button"
                    variant="ghost"
                    aria-expanded={secondary === "declined"}
                    aria-controls="proposal-secondary-declined"
                    onClick={(event) => {
                      trigger.current = event.currentTarget;
                      setSecondary("declined");
                    }}
                  >
                    {t("proposals.decline")}
                  </Button>
                }
                {secondary && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      setSecondary(null);
                      trigger.current?.focus();
                    }}
                  >
                    {t("common.cancel")}
                  </Button>
                )}
              </div>
              {secondary === "rescheduled" && (
                <Form
                  id="proposal-secondary-rescheduled"
                  key={`suggest-${proposal.revision}`}
                  action={decision_url}
                  method="post"
                  className="space-y-3 rounded-lg border p-4"
                >
                  {({ processing }) => (
                    <>
                      <input
                        type="hidden"
                        name="revision"
                        value={proposal.revision}
                      />
                      <input
                        type="hidden"
                        name="decision[action]"
                        value="rescheduled"
                      />
                      <Label htmlFor="proposal-alternative">
                        {t("proposals.alternative")}
                      </Label>
                      <Textarea
                        autoFocus
                        id="proposal-alternative"
                        name="decision[suggested_time]"
                        required
                        maxLength={10000}
                      />
                      <p className="text-muted-foreground text-sm">
                        {t("proposals.suggestion_help")}
                      </p>
                      <Button
                        type="submit"
                        variant="outline"
                        disabled={processing}
                      >
                        {t("proposals.suggest")}
                      </Button>
                    </>
                  )}
                </Form>
              )}
              {secondary === "declined" && (
                <Form
                  id="proposal-secondary-declined"
                  key={`decline-${proposal.revision}`}
                  action={decision_url}
                  method="post"
                  className="space-y-3 rounded-lg border p-4"
                >
                  {({ processing }) => (
                    <>
                      <input
                        type="hidden"
                        name="revision"
                        value={proposal.revision}
                      />
                      <input
                        type="hidden"
                        name="decision[action]"
                        value="declined"
                      />
                      <Label htmlFor="proposal-decline-note">
                        {t("proposals.decline_note")}
                      </Label>
                      <Textarea
                        autoFocus
                        id="proposal-decline-note"
                        name="decision[note]"
                        maxLength={10000}
                      />
                      <Button
                        type="submit"
                        variant="outline"
                        disabled={processing}
                      >
                        {t("proposals.decline")}
                      </Button>
                    </>
                  )}
                </Form>
              )}
            </section>
          ) : (
            !proposal.requires_republication && (
              <p role="status" className="rounded-md border p-4">
                {t("proposals.read_only")}
              </p>
            )
          )}
        </>
      )}
      <p className="text-muted-foreground text-sm">
        {t("proposals.view_help")}
      </p>
    </PageCanvas>
  );
}
